/**
 * The one shape a destructive action is allowed to take in this portal.
 *
 * Rules it enforces, so that no screen has to remember them:
 *
 *  - It is never `button.primary`. Outlined red, and it sits away from the read
 *    controls. The button an operator presses fifty times a day and the button
 *    that spends or destroys must not be neighbours of equal weight.
 *  - The confirmation names the specific thing, in full, and makes you type it.
 *    "Are you sure?" is answered "yes" by reflex; typing
 *    `8a9ac7b6-0b7d-45e6-9937-60b2af450a5c` cannot be. It is also the check that
 *    catches the real error mode here — the right action on the wrong row.
 *  - It shows the consequences as facts about THIS row (whose family, what
 *    request, how many credits, how long it has been running), not as generic
 *    warning copy.
 *  - A reason is mandatory. Every mutating path in `admin.py` requires one
 *    server-side and writes it to the ledger with the operator's identity; a
 *    dialog that lets you submit without one just produces a 400 after the fact.
 *
 * `window.confirm` does none of this. It cannot render the facts, cannot require
 * typing, cannot require a reason, and looks identical to every other confirm a
 * person clicks through in a day.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { message } from './async';

export function DangerAction({
  label,
  title,
  confirmValue,
  confirmLabel,
  facts,
  consequences,
  warning,
  reasonPlaceholder = 'reason (recorded, and required by the API)',
  disabled,
  disabledNote,
  onConfirm,
  onDone,
}: {
  label: string;
  title: string;
  /** The exact string the operator must type. The id of the thing, always. */
  confirmValue: string;
  confirmLabel: string;
  facts: Array<[string, ReactNode]>;
  consequences: ReactNode;
  warning?: ReactNode;
  reasonPlaceholder?: string;
  disabled?: boolean;
  disabledNote?: ReactNode;
  onConfirm: (reason: string) => Promise<string>;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy]);

  if (done) {
    return (
      <div className="notice">
        <b>{done}</b>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="toolbar">
        <button className="destructive" disabled>
          {label}
        </button>
        {disabledNote && <span className="muted small">{disabledNote}</span>}
      </div>
    );
  }

  const armed = typed.trim() === confirmValue && reason.trim().length > 0 && !busy;

  const go = async () => {
    setBusy(true);
    setError('');
    try {
      const detail = await onConfirm(reason.trim());
      setDone(detail);
      setOpen(false);
      onDone?.();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="destructive" onClick={() => setOpen(true)}>
        {label}
      </button>
      {open && (
        <div
          className="scrim"
          onClick={(e) => {
            // Clicking the backdrop closes, but only from the backdrop itself —
            // a click that started inside the dialog and drifted out must not
            // discard what was typed.
            if (e.target === e.currentTarget && !busy) setOpen(false);
          }}
        >
          <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
            <h2>{title}</h2>
            <dl className="facts">
              {facts.map(([key, value]) => (
                <div key={key} style={{ display: 'contents' }}>
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <div className="notice notice-warn">{consequences}</div>
            {warning && <div className="notice notice-bad">{warning}</div>}

            <label className="small muted" htmlFor="danger-reason">
              {reasonPlaceholder}
            </label>
            <input
              id="danger-reason"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
            />

            <label className="small muted" htmlFor="danger-confirm">
              {confirmLabel}
            </label>
            <input
              id="danger-confirm"
              className="mono"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmValue}
              spellCheck={false}
              autoComplete="off"
            />

            {error && <p className="err small">{error}</p>}

            <div className="buttons">
              <button onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </button>
              <button className="destructive" onClick={() => void go()} disabled={!armed}>
                {busy ? 'working…' : label}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
